import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const EMPTY = { current: "", next: "", confirm: "" };

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(EMPTY); setShow(false); } }, [open]);

  const save = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (form.next !== form.confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      if (data.token) localStorage.setItem("smm_token", data.token);
      toast.success("Password changed. Other devices have been signed out.");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not change password"); }
    finally { setBusy(false); }
  };

  const field = (key, label, testid) => (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{label}</Label>
      <Input type={show ? "text" : "password"} value={form[key]} required autoComplete={key === "current" ? "current-password" : "new-password"}
        onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))} data-testid={testid} className="bg-[#FFFFFF] border-[#E4E7EC]" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-neutral-200 max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription className="text-xs">Use at least 8 characters. You'll stay signed in here; other devices will be signed out.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {field("current", "Current password", "cp-current")}
          {field("next", "New password", "cp-new")}
          {field("confirm", "Confirm new password", "cp-confirm")}
          <button type="button" onClick={() => setShow(s => !s)} className="text-xs text-neutral-600 hover:text-orange-600 flex items-center gap-1" data-testid="cp-toggle-show">
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? "Hide" : "Show"} passwords
          </button>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="cp-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cp-save">{busy ? "Saving..." : "Update password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
